import type { AppSession } from './session-types';
import { isNativePlatform } from './is-native';
import { nativeAppStorageId } from '../native-app-id';

const CACHE_KEY = isNativePlatform()
  ? `${nativeAppStorageId()}:app-session`
  : 'couplespace:app-session';
/** Keep the app usable offline after at least one successful online session. */
const SESSION_TTL_MS = 7 * 24 * 60 * 60 * 1000;

type CachedSession = {
  user: AppSession['user'];
  expiresAt: number;
};

export function cacheAppSession(user: AppSession['user']): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  const payload: CachedSession = {
    user,
    expiresAt: Date.now() + SESSION_TTL_MS,
  };
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
  } catch {
    // quota / private mode
  }
}

export function readCachedAppSession(): AppSession['user'] | null {
  if (typeof localStorage === 'undefined') {
    return null;
  }
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as CachedSession;
    if (!parsed?.user?.id || typeof parsed.expiresAt !== 'number') {
      return null;
    }
    if (Date.now() > parsed.expiresAt) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    return parsed.user;
  } catch {
    return null;
  }
}

export function clearCachedAppSession(): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    // ignore
  }
}
